import { useMemo, useState } from "react";
import { isPlanned, type ProjectSummary } from "@joogoos/contracts";
import { Icon } from "../ui/icons.js";

/** How a project's state reads in the rail and over the chat. Colour is the dot; label is the second line. */
const STATE: Record<string, { dot: string; label: string }> = {
  chatting: { dot: "bg-wait", label: "draft" },
  scripted: { dot: "bg-coral", label: "making it" },
  generating: { dot: "bg-coral", label: "making it" },
  over_budget: { dot: "bg-bad", label: "stopped at its cap" },
  failed: { dot: "bg-bad", label: "stopped · try again" },
  rendered: { dot: "bg-good", label: "rendered" },
  published: { dot: "bg-good", label: "published" },
  archived: { dot: "bg-wait", label: "archived" },
};

/** A project's status in the words the rail uses, never the database's word for it. */
export const statusLabel = (status: string) => STATE[status]?.label ?? status;

/**
 * Zone 1 (docs/03): a worklist, not a history, in one of three states.
 *
 * It used to be everything the business had ever made, newest first, which is a column you stop reading by the
 * third week — and it is the same list Content holds, in less room. So the top of it is now **Waiting for you**:
 * the drafts nobody came back to and anything stopped at its cap. Under that, today and this week. Everything
 * older is counted, and the rail says so instead of listing it.
 *
 * The three modes are what keep it from being in the way. **Docked** is where it starts, and starting a new post leaves
 * it there (until 2026-09-12 that collapsed it). The owner can collapse it to a **strip**, which is remembered. Opening
 * it from the strip floats it back as an **overlay** rather than pushing the page sideways — looking something up is
 * not a layout change, and a list that reflows the thing you were reading is a list you stop opening.
 *
 * A phone has no room for a column or a strip, so there the rail is only ever a **drawer** over the screen, opened from
 * the Studio's header and put away by picking something or by its own close button (03 §layout).
 */
export type RailMode = "docked" | "strip" | "overlay" | "drawer";

export interface RailProps {
  mode: RailMode;
  projects: ProjectSummary[] | undefined;
  activeId: string | null;
  onPick: (id: string) => void;
  onNew: () => void;
  /** Docked → strip, strip → overlay, overlay → docked. One button, one obvious next state. */
  onToggle: () => void;
  onSeeAll?: (() => void) | undefined;
  busy?: boolean | undefined;
  /** Delete a draft (docs/03). Offered only on a row the API marks deletable, and only after asking. */
  onDelete?: ((id: string) => void) | undefined;
  /** Prepare my week, above everything else (docs/03 §prepare my week). Its pieces are listed there and not below. */
  week?: React.ReactNode;
}

export function Rail(props: RailProps) {
  const { mode, projects, activeId, onPick, onNew, onToggle, onSeeAll, busy, onDelete, week } = props;
  const { waiting, groups, older } = useMemo(() => split(projects ?? []), [projects]);

  if (mode === "strip") {
    return (
      <aside className="w-14 bg-rail border-r border-line flex flex-col items-center gap-2.5 py-3 shrink-0">
        <button onClick={onNew} disabled={busy} title="New post" className="w-8.5 h-8.5 rounded-[9px] bg-coral text-white grid place-items-center disabled:opacity-50">
          <Icon name="plus" size={17} />
        </button>
        {[...waiting, ...groups.flatMap(([, items]) => items)].slice(0, 8).map((p) => (
          <button
            key={p.id}
            onClick={() => onPick(p.id)}
            title={p.needs ? `${p.title} — ${p.needs}` : p.title}
            className={`relative w-8 h-8 rounded-[7px] bg-line grid place-items-center text-muted ${p.id === activeId ? "ring-2 ring-coral" : ""}`}
          >
            <Icon name={p.kind === "post" ? "image" : "play"} size={13} />
            {p.needs ? <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-coral border border-rail" /> : null}
          </button>
        ))}
        <button onClick={onToggle} title="Show your posts" className="mt-auto text-faint hover:text-ink">
          <Icon name="chevronRight" />
        </button>
      </aside>
    );
  }

  const overlay = mode === "overlay";
  const drawer = mode === "drawer";
  return (
    <aside
      className={
        drawer
          ? "absolute z-50 left-0 top-0 bottom-0 w-[min(18rem,85vw)] bg-rail border-r border-line flex flex-col min-h-0 shadow-[0_10px_40px_rgba(23,23,26,.22)]"
          : overlay
          ? "absolute z-50 left-14 top-0 bottom-0 w-56 bg-rail border-r border-line flex flex-col min-h-0 shadow-[0_10px_40px_rgba(23,23,26,.22)]"
          : "w-56 bg-rail border-r border-line flex flex-col min-h-0 shrink-0"
      }
    >
      <div className="p-3 pb-2 flex flex-col gap-2">
        <button
          onClick={onNew}
          disabled={busy}
          className="flex items-center justify-center gap-2 py-2.5 rounded-lg bg-coral text-white text-sm font-medium hover:bg-coral-2 disabled:opacity-50"
        >
          <Icon name="plus" /> New post
        </button>
      </div>

      <div className="flex-1 overflow-y-auto pb-2">
        {week}
        {waiting.length ? (
          <Group label={`Waiting for you · ${waiting.length}`} accent>
            {waiting.map((p) => <Row key={p.id} p={p} on={p.id === activeId} onPick={onPick} onDelete={onDelete} />)}
          </Group>
        ) : null}
        {groups.map(([label, items]) => (
          <Group key={label} label={label}>
            {items.map((p) => <Row key={p.id} p={p} on={p.id === activeId} onPick={onPick} onDelete={onDelete} />)}
          </Group>
        ))}
        {projects && !projects.length ? (
          <p className="px-3.5 py-4 text-xs text-muted leading-relaxed">Nothing here yet. Say what you want to make, or pick something from the gallery.</p>
        ) : null}
        {older ? (
          <button onClick={onSeeAll} className="w-full text-left px-3.5 pt-3 pb-1 text-[11.5px] text-muted hover:text-ink">
            {older} older {older === 1 ? "post" : "posts"} in Content →
          </button>
        ) : null}
      </div>

      <div className="px-3.5 py-2.5 border-t border-line flex items-center gap-2">
        {/* The footer used to carry an "autopilot off" badge. Autopilot does not exist, and a badge for the
            absence of a feature is an advertisement for it. */}
        <span className="text-[11.5px] text-muted">{projects?.length ?? 0} posts</span>
        <button
          onClick={onToggle}
          title={drawer ? "Close" : overlay ? "Keep it open" : "Collapse"}
          aria-label={drawer ? "Close" : overlay ? "Keep it open" : "Collapse"}
          className="ml-auto text-faint hover:text-ink"
        >
          <Icon name={drawer ? "x" : overlay ? "pin" : "chevronLeft"} size={15} />
        </button>
      </div>
    </aside>
  );
}

function Group({ label, accent, children }: { label: string; accent?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className={`px-3.5 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[.08em] ${accent ? "text-coral" : "text-faint"}`}>{label}</div>
      {children}
    </div>
  );
}

function Row({ p, on, onPick, onDelete }: { p: ProjectSummary; on: boolean; onPick: (id: string) => void; onDelete?: ((id: string) => void) | undefined }) {
  const st = STATE[p.status] ?? { dot: "bg-wait", label: p.status };
  const [asking, setAsking] = useState(false);
  const canDelete = !!onDelete && p.deletable;

  // Deleting cannot be undone, so the row asks first, in place, rather than opening a dialog over the page.
  if (asking && canDelete) {
    return (
      <div className="mx-1.5 w-[calc(100%-0.75rem)] flex items-center gap-2 px-2.5 py-1.5 rounded-[9px] bg-panel border border-line">
        <span className="min-w-0 flex-1 text-[12px] truncate">Delete “{p.title}”?</span>
        <button onClick={() => { setAsking(false); onDelete(p.id); }} className="px-2 py-1 rounded-md bg-bad text-white text-[11px] font-semibold">Delete</button>
        <button onClick={() => setAsking(false)} className="px-2 py-1 rounded-md text-[11px] text-muted hover:text-ink">Keep</button>
      </div>
    );
  }

  return (
    <div className="group relative">
      <button
        onClick={() => onPick(p.id)}
        className={`w-full flex items-center gap-2.5 mx-1.5 px-2.5 py-1.5 rounded-[9px] text-left w-[calc(100%-0.75rem)] ${
          on ? "bg-panel border border-line shadow-[0_1px_2px_rgba(23,23,26,.08)]" : "hover:bg-panel/60"
        }`}
      >
        <span className="w-7 h-7 rounded-md bg-line grid place-items-center text-muted shrink-0">
          <Icon name={p.kind === "post" ? "image" : "play"} size={12} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-[12.5px] truncate ${on ? "font-semibold" : "font-medium"}`}>{p.title}</span>
          <span className="flex items-center gap-1.5 text-[10.5px] text-muted">
            <span className={`w-1.5 h-1.5 rounded-full ${p.needs ? "bg-coral" : st.dot}`} />
            {/* What it needs, when it needs something: a reason is worth more than a status anyone can see. */}
            {p.needs ?? st.label} · {when(p.updatedAt)}
          </span>
        </span>
        {on && !canDelete ? <Icon name="copy" size={13} className="text-faint shrink-0" /> : null}
      </button>
      {canDelete ? (
        // Only for a draft nothing was made from: what was generated is never one click from gone.
        <button
          onClick={() => setAsking(true)}
          title="Delete this draft"
          aria-label={`Delete ${p.title}`}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-md text-faint hover:text-bad hover:bg-page opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <Icon name="trash" size={13} />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Waiting first, then now, then this week. Anything older is counted, not listed. A planned piece of the week is in
 * none of them: it is listed in the week, by its day, and twice would be once too many.
 */
function split(projects: ProjectSummary[]) {
  const sorted = projects.filter((p) => !isPlanned(p)).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const waiting = sorted.filter((p) => p.needs);
  const rest = sorted.filter((p) => !p.needs);
  const day = 86_400_000;
  const now = Date.now();
  const buckets: Array<[string, ProjectSummary[]]> = [["Today", []], ["This week", []]];
  let older = 0;
  for (const p of rest) {
    const age = now - Date.parse(p.updatedAt);
    if (age < day) buckets[0]![1].push(p);
    else if (age < 7 * day) buckets[1]![1].push(p);
    else older++;
  }
  return { waiting, groups: buckets.filter(([, items]) => items.length), older };
}

/** How long ago something was touched, in the words the rail and the Overview both use. */
export function when(iso: string) {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
